// src/pages/tour-packages/hooks/useTourPackageEditForm.ts

import type { BaseSyntheticEvent } from "react";
import {
  useEffect,  
  useRef,
} from "react";
import {
  useForm,
  useFieldArray,
  type UseFormReturn,
  type UseFieldArrayReturn,
} from "react-hook-form";

import type { UpdateTourWithDetailsPayload } from "@/api/features/tours";
import type {
  TourItineraryDayInput,
  TourDetail,
} from "@/api/features/tours/types";

import type { TourPackageFormValues } from "./useTourPackageCreateForm";
import {
  buildRatePrices,
  getMissingModePriceFields,
  ALL_MODE_PRICE_FIELDS,
} from "./tourRatePricing";

/* =========================================
 * Hook options & return types
 * ======================================= */

export type UseTourPackageEditFormOptions<TResult = unknown> = {
  /**
   * Full tour detail as returned by GET /api/v1/tours/:id
   * Can be null/undefined initially; hook will hydrate when it becomes available.
   */
  defaultValues?: TourDetail | null;

  /**
   * Called with payload suitable for PUT /api/v1/tours/:id
   */
  onSubmit: (payload: UpdateTourWithDetailsPayload) => Promise<TResult>;

  /**
   * Debug logs
   */
  debug?: boolean;
};

export type UseTourPackageEditFormResult = {
  form: UseFormReturn<TourPackageFormValues>;
  handleSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  isSubmitting: boolean;

  citiesFieldArray: UseFieldArrayReturn<TourPackageFormValues, "cities", "id">;
  tourRatesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "tourRates",
    "id"
  >;
  itineraryDaysFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "itineraryDays",
    "id"
  >;
  inclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "inclusions",
    "id"
  >;
  exclusionsFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "exclusions",
    "id"
  >;
  cancellationPoliciesFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "cancellationPolicies",
    "id"
  >;
  hotelsFieldArray: UseFieldArrayReturn<TourPackageFormValues, "hotels", "id">;
  imagesFieldArray: UseFieldArrayReturn<TourPackageFormValues, "images", "id">;
  departuresFieldArray: UseFieldArrayReturn<
    TourPackageFormValues,
    "departures",
    "id"
  >;
};

/* =========================================
 * Main hook – EDIT (single source of truth)
 * ======================================= */

export function useTourPackageEditForm<TResult = unknown>(
  options: UseTourPackageEditFormOptions<TResult>
): UseTourPackageEditFormResult {
  const { defaultValues, onSubmit } = options;

  // const log = (...args: any[]) => {
  //   if (!debug) return;
  //   // eslint-disable-next-line no-console
  //   console.log("[EDIT_FORM]", ...args);
  // };

  // Build an initial "empty" form. We will hydrate via effect when defaultValues arrives.
  const form = useForm<TourPackageFormValues>({
    defaultValues: buildEmptyFormValues(),
    shouldFocusError: false,
  });

  const { control, handleSubmit: rhfHandleSubmit, formState, setError, clearErrors } = form;

  const citiesFieldArray = useFieldArray({ control, name: "cities" });
  const tourRatesFieldArray = useFieldArray({ control, name: "tourRates" });
  const itineraryDaysFieldArray = useFieldArray({ control, name: "itineraryDays" });
  const inclusionsFieldArray = useFieldArray({ control, name: "inclusions" });
  const exclusionsFieldArray = useFieldArray({ control, name: "exclusions" });
  const cancellationPoliciesFieldArray = useFieldArray({
    control,
    name: "cancellationPolicies",
  });
  const hotelsFieldArray = useFieldArray({ control, name: "hotels" });
  const imagesFieldArray = useFieldArray({ control, name: "images" });
  const departuresFieldArray = useFieldArray({ control, name: "departures" });

  const isSubmitting = formState.isSubmitting;

  /**
   * Hydration (only once per updatedAt/id)
   */
  const hydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // log("hydration effect", { hasDefaultValues: !!defaultValues });

    if (!defaultValues) return;

    const key = String(
      (defaultValues as any).updatedAt ?? (defaultValues as any).id ?? "unknown"
    );

    if (hydratedKeyRef.current === key) {
      // log("skip hydration (already hydrated)", key);
      return;
    }

    hydratedKeyRef.current = key;

    const values = mapTourDetailToFormValues(defaultValues);

    // log("hydrate key", key);
    // log("mapped tour.hasFixedDepartures", values?.tour?.hasFixedDepartures);
    // log("mapped departures", values?.departures);

    // 1) reset everything
    form.reset(values);

    // 2) replace field arrays (keeps RHF fieldArray state consistent)
    citiesFieldArray.replace(values.cities ?? []);
    tourRatesFieldArray.replace(values.tourRates ?? []);
    itineraryDaysFieldArray.replace(values.itineraryDays ?? []);
    inclusionsFieldArray.replace(values.inclusions ?? []);
    exclusionsFieldArray.replace(values.exclusions ?? []);
    cancellationPoliciesFieldArray.replace(values.cancellationPolicies ?? []);
    hotelsFieldArray.replace(values.hotels ?? []);
    imagesFieldArray.replace(values.images ?? []);
    departuresFieldArray.replace((values as any).departures ?? []);

    // 3) force-apply departures next tick (covers date inputs not registered yet)
    setTimeout(() => {
      const deps = (values as any).departures ?? [];

      // log("force-apply departures (next tick)", deps);

      form.setValue("tour.hasFixedDepartures", values.tour.hasFixedDepartures, {
        shouldDirty: false,
        shouldValidate: false,
      });

      form.setValue("departures", deps, {
        shouldDirty: false,
        shouldValidate: false,
      });

      departuresFieldArray.replace(deps);

      // log("after force set tour.hasFixedDepartures", form.getValues("tour.hasFixedDepartures"));
      // log("after force set departures", form.getValues("departures"));

    }, 0);
  }, [defaultValues]);

  const handleSubmit = rhfHandleSubmit(async (values) => {
    // Reset the errors we manage here so fixed fields clear.
    clearErrors("root.server");
    clearErrors("tour.currencyCode");
    clearErrors(ALL_MODE_PRICE_FIELDS.map((k) => `tourRates.0.${k}` as any));

    let hasError = false;

    // Currency is required.
    if (!String(values.tour.currencyCode ?? "").trim()) {
      setError("tour.currencyCode", {
        type: "required",
        message: "Select a currency.",
      });
      hasError = true;
    }

    // All price fields for the active mode are required — flag each missing one
    // so it appears in the error list and gets highlighted in red.
    for (const key of getMissingModePriceFields(values)) {
      setError(`tourRates.0.${key}` as any, {
        type: "required",
        message: "Required",
      });
      hasError = true;
    }

    if (hasError) return;

    try {
      const payload = buildPayloadFromFormValues(values);

      await onSubmit(payload);
    } catch (err) {
      // log("submit error", err);
      throw err;
    }
  });

  return {
    form,
    handleSubmit,
    isSubmitting,
    citiesFieldArray,
    tourRatesFieldArray,
    itineraryDaysFieldArray,
    inclusionsFieldArray,
    exclusionsFieldArray,
    cancellationPoliciesFieldArray,
    hotelsFieldArray,
    imagesFieldArray,
    departuresFieldArray,
  };
}

/* =========================================
 * Helpers – defaults
 * ======================================= */

function buildEmptyFormValues(): TourPackageFormValues {
  return {
    tour: {
      name: "",
      tourTypeId: "",
      supplierId: "",
      nights: "",
      days: "",
      hasFixedDepartures: false,
      isPrivateTour: false,
      tourMode: "GROUP",
      isMultiCity: false,
      cityCount: "",
      currencyCode: "",
      status: "DRAFT",
    },
    tourRates: [
      {
        rateType: "BASE",
        status: "ACTIVE",
        priority: 0,
        travelFrom: "",
        travelTo: "",
        bookFrom: "",
        bookTo: "",
        currencyCode: "",
        priceDouble: "",
        priceTriple: "",
        priceChildWb: "",
        priceChildWob: "",
        priceInfant: "",
        price2Pax: "",
        price3Pax: "",
        price4Pax: "",
        price5Pax: "",
        price6Pax: "",
        isTaxInclusive: true,
        label: "",
        notes: "",
      },
    ],
    cities: [{ countryCode: "", country: "", state: "", city: "", nightsInCity: "" }],
    itineraryDays: [],
    inclusions: [],
    exclusions: [],
    cancellationPolicies: [],
    hotels: [],
    images: [],
    departures: [],
  };
}

/* =========================================
 * Helpers – map API detail → form values
 * (ONLY PLACE that maps for edit mode)
 * ======================================= */

function mapTourDetailToFormValues(tour: TourDetail): TourPackageFormValues {
  const toNumberOrEmpty = (value: string | number | null | undefined): number | "" => {
    if (value === null || value === undefined || value === "") return "";
    const n = typeof value === "string" ? Number(value) : value;
    return Number.isFinite(n) ? (n as number) : "";
  };

  const upper = (v: any): string =>
    typeof v === "string" ? v.trim().toUpperCase() : "";

  const safeStr = (v: any): string =>
    typeof v === "string" ? v : v == null ? "" : String(v);

  const rawDepartures = (((tour as any).departures ?? []) as any[]) ?? [];
  const mappedDepartures = rawDepartures.map((d: any) => ({
    departureDate: d?.departureDate ? String(d.departureDate).slice(0, 10) : "",
    capacity: typeof d?.capacity === "number" ? d.capacity : "",
    status: d?.status ?? "ACTIVE",
  }));

  // Mode of tour is the single driver: GROUP ⇒ fixed departures, PRIVATE ⇒ private.
  // Derive mode from the stored tour_mode; legacy rows without it fall back to the
  // old flags (isPrivateTour, or any saved departures implying a group/FD tour).
  const isPrivateDerived = !!(tour as any).isPrivateTour;
  const hadFixedDepartures = Boolean(
    (tour as any).hasFixedDepartures ?? (mappedDepartures.length > 0)
  );
  const tourModeDerived: "GROUP" | "PRIVATE" =
    (tour as any).tourMode === "PRIVATE" || (tour as any).tourMode === "GROUP"
      ? (tour as any).tourMode
      : isPrivateDerived && !hadFixedDepartures
        ? "PRIVATE"
        : "GROUP";

  const hasFixedDeparturesDerived = tourModeDerived === "GROUP";

  return {
    tour: {
      name: safeStr((tour as any).name),
      tourTypeId: (tour as any).tourTypeId ?? "",
      supplierId: (tour as any).supplierId ?? "",
      nights: (tour as any).nights ?? "",
      days: (tour as any).days ?? "",
      hasFixedDepartures: hasFixedDeparturesDerived,
      isPrivateTour: tourModeDerived === "PRIVATE",
      tourMode: tourModeDerived,
      isMultiCity: !!(tour as any).isMultiCity,
      cityCount: (tour as any).cityCount ?? "",
      currencyCode: upper((tour as any).currencyCode) || "",
      status: (tour as any).status ?? "DRAFT",
    },
    tourRates:
      (((tour as any).rates ?? []) as any[]).map((r) => ({
        rateType: (r?.rateType ?? "BASE") as "BASE" | "PROMO",
        status: (r?.status ?? "ACTIVE") as "ACTIVE" | "INACTIVE",
        priority: typeof r?.priority === "number" ? r.priority : 0,
        travelFrom: r?.travelFrom ? String(r.travelFrom).slice(0, 10) : "",
        travelTo: r?.travelTo ? String(r.travelTo).slice(0, 10) : "",
        bookFrom: r?.bookFrom ? String(r.bookFrom).slice(0, 10) : "",
        bookTo: r?.bookTo ? String(r.bookTo).slice(0, 10) : "",
        currencyCode: upper(r?.currencyCode) || upper((tour as any).currencyCode) || "",
        priceDouble: toNumberOrEmpty(r?.priceDouble),
        priceTriple: toNumberOrEmpty(r?.priceTriple),
        priceChildWb: toNumberOrEmpty(r?.priceChildWb),
        priceChildWob: toNumberOrEmpty(r?.priceChildWob),
        priceInfant: toNumberOrEmpty(r?.priceInfant),
        price2Pax: toNumberOrEmpty(r?.price2Pax),
        price3Pax: toNumberOrEmpty(r?.price3Pax),
        price4Pax: toNumberOrEmpty(r?.price4Pax),
        price5Pax: toNumberOrEmpty(r?.price5Pax),
        price6Pax: toNumberOrEmpty(r?.price6Pax),
        isTaxInclusive:
          typeof r?.isTaxInclusive === "boolean"
            ? r.isTaxInclusive
            : !!(tour as any).isTaxInclusive,
        label: safeStr(r?.label),
        notes: safeStr(r?.notes),
      })) || [],

    cities: (((tour as any).cities ?? []) as any[]).map((c) => ({
      countryCode: upper(c?.countryCode) || "",
      country: safeStr(c?.country),
      state: safeStr(c?.state),
      city: safeStr(c?.city),
      nightsInCity: typeof c?.nightsInCity === "number" ? c.nightsInCity : "",
    })),

    itineraryDays: (((tour as any).itineraryDays ?? []) as any[]).map((d) => ({
      dayNumber: d?.dayNumber ?? "",
      title: safeStr(d?.title),
      description: safeStr(d?.description),
    })),

    inclusions: (((tour as any).inclusions ?? []) as any[]).map((i) => ({
      sortOrder: i?.sortOrder ?? "",
      label: safeStr(i?.label),
      description: safeStr(i?.description),
    })),

    exclusions: (((tour as any).exclusions ?? []) as any[]).map((e) => ({
      sortOrder: e?.sortOrder ?? "",
      label: safeStr(e?.label),
      description: safeStr(e?.description),
    })),

    cancellationPolicies: (((tour as any).cancellationPolicies ?? []) as any[]).map(
      (c) => ({
        label: safeStr(c?.label),
        description: safeStr(c?.description),
      })
    ),

    hotels: (((tour as any).hotels ?? []) as any[]).map((h) => ({
      hotelName: safeStr(h?.hotelName),
      hotelCategory: safeStr(h?.hotelCategory),
      addressLine1: safeStr(h?.addressLine1),
      addressLine2: safeStr(h?.addressLine2),
      city: safeStr(h?.city),
      state: safeStr(h?.state),
      country: safeStr(h?.country),
      countryCode: upper(h?.countryCode) || "",
      postalCode: safeStr(h?.postalCode),
      roomType: safeStr(h?.roomType),
      mealPlan: h?.mealPlan ?? "BB",
    })),

    images: (((tour as any).images ?? []) as any[]).map((img) => ({
      imageUrl: safeStr(img?.imageUrl),
      caption: safeStr(img?.caption),
      sortOrder: img?.sortOrder ?? "",
    })),

    departures: mappedDepartures,
  };
}

/* =========================================
 * Helpers – payload mapping (safe trims)
 * ======================================= */

function buildPayloadFromFormValues(
  values: TourPackageFormValues
): UpdateTourWithDetailsPayload {
  const itineraryDaysPayload: TourItineraryDayInput[] = values.itineraryDays
    .map((d) => {
      const dayNumber = Number(d.dayNumber || 0);

      return {
        dayNumber,
        title: safeTrim(d?.title) || undefined,
        description: safeTrim(d?.description) || undefined,
      } satisfies TourItineraryDayInput;
    })
    .filter((d) => d.dayNumber > 0 && (d.title || d.description));

  const cityCountDerived =
    typeof values.tour.cityCount === "number"
      ? values.tour.cityCount
      : Number(values.tour.cityCount || values.cities.length || 1);

  const departuresPayload = (values.departures ?? [])
    .map((d) => {
      const rawDate: any = (d as any)?.departureDate;
      const departureDate = normalizeToYmd(rawDate);

      if (!departureDate) return null;

      const capRaw = (d as any)?.capacity;
      const capacity =
        capRaw === "" || capRaw == null || Number.isNaN(capRaw)
          ? null
          : Number(capRaw);

      const status = safeUpper((d as any)?.status || "ACTIVE");

      return { departureDate, capacity, status };
    })
    .filter(Boolean) as Array<{
    departureDate: string;
    capacity: number | null;
    status: string | null;
  }>;

  // For fixed-departure tours the Travel window is hidden in the UI; derive the
  // rate's travel window from the FD dates so the rate (and its pricing) persists.
  const isFD = values.tour.hasFixedDepartures;
  const sortedDepartureDates = departuresPayload
    .map((d) => d.departureDate)
    .filter(Boolean)
    .sort();
  const fdTravelFrom = sortedDepartureDates[0] ?? "";
  const fdTravelTo = sortedDepartureDates[sortedDepartureDates.length - 1] ?? "";

  return {
    tour: {
      name: safeTrim(values.tour.name),
      tourTypeId: Number(values.tour.tourTypeId),
      supplierId: Number(values.tour.supplierId),
      nights: Number(values.tour.nights),
      days: Number(values.tour.days),
      hasFixedDepartures: values.tour.hasFixedDepartures,
      isPrivateTour: values.tour.isPrivateTour,
      tourMode: values.tour.tourMode,
      isMultiCity: values.tour.isMultiCity,
      cityCount: cityCountDerived,
      currencyCode: safeUpper(values.tour.currencyCode),
      status: values.tour.status,
    },

    cities: (values.cities ?? [])
      .map((c, index) => ({
        countryCode: safeUpper((c as any).countryCode) || undefined,
        country: safeTrim((c as any).country),
        state: safeTrim((c as any).state),
        city: safeTrim((c as any).city),
        nightsInCity: (c as any).nightsInCity ? Number((c as any).nightsInCity) : undefined,
        sortOrder: index + 1,
      }))
      .filter((c) => c.city || c.country),

    itineraryDays: itineraryDaysPayload,

    inclusions: (values.inclusions ?? [])
      .filter((i) => safeTrim((i as any).label))
      .map((i, index) => ({
        sortOrder: index + 1,
        label: safeTrim((i as any).label),
        description: safeTrim((i as any).description) || undefined,
      })),

    exclusions: (values.exclusions ?? [])
      .filter((e) => safeTrim((e as any).label))
      .map((e, index) => ({
        sortOrder: index + 1,
        label: safeTrim((e as any).label),
        description: safeTrim((e as any).description) || undefined,
      })),

    cancellationPolicies: (values.cancellationPolicies ?? [])
      .filter((c) => safeTrim((c as any).label))
      .map((c, index) => ({
        sortOrder: index + 1,
        label: safeTrim((c as any).label),
        description: safeTrim((c as any).description) || undefined,
      })),

    hotels: (values.hotels ?? [])
      .map((h) => ({
        countryCode: safeUpper((h as any).countryCode) || undefined,
        hotelName: safeTrim((h as any).hotelName),
        hotelCategory: safeTrim((h as any).hotelCategory),
        addressLine1: safeTrim((h as any).addressLine1),
        addressLine2: safeTrim((h as any).addressLine2),
        city: safeTrim((h as any).city),
        state: safeTrim((h as any).state),
        country: safeTrim((h as any).country),
        postalCode: safeTrim((h as any).postalCode),
        roomType: safeTrim((h as any).roomType),
        mealPlan: (h as any).mealPlan,
      }))
      .filter((h) => h.hotelName),

    images: (values.images ?? [])
      .map((img, index) => ({
        imageUrl: safeTrim((img as any).imageUrl),
        caption: safeTrim((img as any).caption),
        sortOrder: (img as any).sortOrder ? Number((img as any).sortOrder) : index + 1,
      }))
      .filter((img) => img.imageUrl),

    departures: values.tour.hasFixedDepartures ? departuresPayload : undefined,
    tourRatesReplace: (values.tourRates ?? [])
      .map((r) => ({
        rateType: r.rateType,
        status: r.status,
        priority: r.priority === "" || r.priority == null ? 0 : Number(r.priority),
        travelFrom: safeTrim(r.travelFrom) || (isFD ? fdTravelFrom : ""),
        travelTo: (safeTrim(r.travelTo) || (isFD ? fdTravelTo : "")) || null,
        bookFrom: safeTrim(r.bookFrom) || null,
        bookTo: safeTrim(r.bookTo) || null,
        // The form has one header Currency field (tour.currencyCode) and no
        // per-rate currency control, so the header is the single source of
        // truth: whatever the admin picks there is what every rate is saved
        // as, on every save — a relabel, not a price conversion. Preferring
        // r.currencyCode here (the rate's PREVIOUSLY saved value, always
        // non-empty once a tour has been saved once) is why changing the
        // header currency looked like it never saved: it never reached the
        // rates, and GET derives the displayed currency from the rates.
        currencyCode: safeUpper(values.tour.currencyCode),
        ...buildRatePrices(r, values.tour.tourMode),
        isTaxInclusive: r.isTaxInclusive,
        label: safeTrim(r.label) || null,
        notes: safeTrim(r.notes) || null,
      }))
      .filter((r) => r.travelFrom && r.priceDouble > 0),
  };
}

function safeTrim(v: unknown): string {
  return typeof v === "string" ? v.trim() : String(v ?? "").trim();
}

function safeUpper(v: unknown): string {
  return safeTrim(v).toUpperCase();
}

function normalizeToYmd(value: unknown): string {
  if (value == null) return "";

  const asString = safeTrim(value);
  if (!asString) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;

  const date =
    value instanceof Date
      ? value
      : new Date(asString);

  if (Number.isNaN(date.getTime())) return asString;
  return date.toISOString().slice(0, 10);
}
